import type { Dictionary } from "@/lib/i18n/get-dictionary";
import type { ProfileFieldId } from "./field-config";

export type FieldCopy = {
	nudgeLabel: string;
	formLabel: string;
	helpText: string;
};

/**
 * English fallback copy. Used when no dictionary is supplied (legacy callers,
 * tests) or when a translation key is missing. Keep these in sync with
 * `src/locales/en/profile.json`.
 */
export const FIELD_COPY_FALLBACK: Record<ProfileFieldId, FieldCopy> = {
	industry: {
		nudgeLabel: "Add industry → better matches",
		formLabel: "Industry",
		helpText: "Tells us which sector to surface jobs from.",
	},
	company: {
		nudgeLabel: "Add your current company",
		formLabel: "Current company",
		helpText: "Helps us avoid showing you jobs from your own employer.",
	},
	experience: {
		nudgeLabel: "Add years of experience",
		formLabel: "Years of experience",
		helpText: "Filters out roles way below or above your level.",
	},
	interests: {
		nudgeLabel: "Pick 1–5 work interests",
		formLabel: "Work interests",
		helpText: "Multi-select — pick the kinds of work you want.",
	},
	roles: {
		nudgeLabel: "Pick preferred roles",
		formLabel: "Preferred roles",
		helpText: "Roles you're aiming for next.",
	},
	aspiration: {
		nudgeLabel: "Share your career goal",
		formLabel: "Career goal",
		helpText: "One sentence about where you want to be in 12 months.",
	},
	linkedin: {
		nudgeLabel: "Unlock 8 pages — add LinkedIn",
		formLabel: "LinkedIn URL",
		helpText: "Adding a profile link unlocks 8 pages of jobs for free.",
	},
	github: {
		nudgeLabel: "Add your GitHub",
		formLabel: "GitHub URL",
		helpText: "For engineering roles — employers appreciate it.",
	},
	portfolio: {
		nudgeLabel: "Add a portfolio",
		formLabel: "Portfolio URL",
		helpText: "Your personal site, case studies, or work samples.",
	},
};

const KEY_MAP: Record<
	ProfileFieldId,
	{ nudge: string; form: string; help: string }
> = {
	industry: {
		nudge: "fieldIndustryNudgeLabel",
		form: "fieldIndustryFormLabel",
		help: "fieldIndustryHelpText",
	},
	company: {
		nudge: "fieldCompanyNudgeLabel",
		form: "fieldCompanyFormLabel",
		help: "fieldCompanyHelpText",
	},
	experience: {
		nudge: "fieldExperienceNudgeLabel",
		form: "fieldExperienceFormLabel",
		help: "fieldExperienceHelpText",
	},
	interests: {
		nudge: "fieldInterestsNudgeLabel",
		form: "fieldInterestsFormLabel",
		help: "fieldInterestsHelpText",
	},
	roles: {
		nudge: "fieldRolesNudgeLabel",
		form: "fieldRolesFormLabel",
		help: "fieldRolesHelpText",
	},
	aspiration: {
		nudge: "fieldAspirationNudgeLabel",
		form: "fieldAspirationFormLabel",
		help: "fieldAspirationHelpText",
	},
	linkedin: {
		nudge: "fieldLinkedinNudgeLabel",
		form: "fieldLinkedinFormLabel",
		help: "fieldLinkedinHelpText",
	},
	github: {
		nudge: "fieldGithubNudgeLabel",
		form: "fieldGithubFormLabel",
		help: "fieldGithubHelpText",
	},
	portfolio: {
		nudge: "fieldPortfolioNudgeLabel",
		form: "fieldPortfolioFormLabel",
		help: "fieldPortfolioHelpText",
	},
};

/**
 * Returns the localized copy for a single profile field, falling back to the
 * English string when the key is missing from the dictionary. Pass `undefined`
 * for `t` to get the EN fallback (useful in tests and at boundaries that
 * don't have a dictionary in scope).
 */
export function getFieldCopy(field: ProfileFieldId, t?: Dictionary): FieldCopy {
	const fallback = FIELD_COPY_FALLBACK[field];
	if (!t) return fallback;
	const keys = KEY_MAP[field];
	return {
		nudgeLabel: t[keys.nudge] ?? fallback.nudgeLabel,
		formLabel: t[keys.form] ?? fallback.formLabel,
		helpText: t[keys.help] ?? fallback.helpText,
	};
}

/**
 * Backward-compatible export — current consumers `import { FIELD_COPY }` and
 * read `FIELD_COPY[field].nudgeLabel`. Keeps that path working while new code
 * adopts `getFieldCopy(field, t)`.
 *
 * @deprecated Prefer `getFieldCopy(field, t)` so locale flows through.
 */
export const FIELD_COPY = FIELD_COPY_FALLBACK;
